import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { PartnerFlatFeeSimulationResponse } from '@interfaces/partners/responses/partner-flat-fee.response';
import { PartnersHttpService } from '@services/http/partners/partners-http.service';
import { SharedModule } from '@src/app/shared/shared.module';
import {
  PARTNER_TERM_YEARS_OPTIONS,
  parsePartnerTermYears,
} from '@src/app/shared/utils/partner-term-years.utils';
import { MenuItem } from 'primeng/api';
import { of } from 'rxjs';
import { catchError, debounceTime, map, startWith, switchMap, tap } from 'rxjs/operators';

/** Valores com que a tela abre: os mesmos da planilha que o comercial usava. */
const INITIAL_TERM_YEARS = '1';
const INITIAL_AMOUNT = 1_000_000;

/** Espera entre a última tecla e a simulação no backend. */
const SIMULATION_DEBOUNCE_MS = 300;

/**
 * Comercial › Calculadora (card 3683).
 *
 * A "Calculadora do Parceiro" da planilha, dentro da plataforma: o parceiro (ou o time interno,
 * conversando com ele) escolhe o prazo e o valor da captação e vê a comissão estimada, com o fee do
 * parceiro fixo em 20% (decisão do Breno, 23/10/2026). Nada é gravado. Desde o card 3745 a tela mostra
 * só a comissão: fee, flat fee da operação, flat fee total e o resumo saíram.
 *
 * A conta é do backend desde o card 3707 (`GET partners/calculator/simulate` para cada cenário,
 * `PartnerFlatFeeCalculator`): a tela só mostra. O `GET partners/calculator` (fee e faixas) deixou de
 * ser chamado no card 3745, quando o fee saiu da tela. O Registrar deal e a tela do deal mostram o flat fee estimado com a mesma tabela.
 */
@Component({
  selector: 'app-partner-calculator',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, SharedModule],
  templateUrl: './partner-calculator.component.html',
  styleUrls: ['./partner-calculator.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PartnerCalculatorComponent {
  private readonly fb = inject(FormBuilder);
  private readonly partnersService = inject(PartnersHttpService);

  readonly breadcrumbs: MenuItem[] = [
    { label: 'Comercial' },
    { label: 'Calculadora', routerLink: '/dashboard/commercial/calculator' },
  ];
  readonly home: MenuItem = { icon: 'pi pi-home', routerLink: '/dashboard/dashboard-management' };

  readonly termOptions = PARTNER_TERM_YEARS_OPTIONS;

  readonly form = this.fb.group({
    termYears: [INITIAL_TERM_YEARS],
    amount: [INITIAL_AMOUNT as number | null],
  });

  /** Prazo escolhido nos botões (valor do `Option`, "1" a "5"). */
  readonly termYears = toSignal(this.form.controls.termYears.valueChanges, {
    initialValue: this.form.controls.termYears.value,
  });

  /** A simulação está a caminho (mostra "Calculando…" sem apagar o número anterior). */
  readonly simulating = signal(false);

  /** A última simulação falhou: a tela avisa em vez de mostrar número velho. */
  readonly simulationFailed = signal(false);

  /** Comissão do cenário escolhido, calculados no backend; `null` sem prazo. */
  readonly simulation = toSignal(
    this.form.valueChanges.pipe(
      startWith(this.form.getRawValue()),
      map(() => this.form.getRawValue()),
      debounceTime(SIMULATION_DEBOUNCE_MS),
      switchMap(({ amount, termYears }) => {
        const term = parsePartnerTermYears(termYears);
        if (term === null) return of(null);
        this.simulating.set(true);
        return this.partnersService.simulateFlatFee(toAmount(amount), term).pipe(
          tap(() => this.simulationFailed.set(false)),
          catchError(() => {
            this.simulationFailed.set(true);
            return of(null);
          })
        );
      }),
      tap(() => this.simulating.set(false)),
      takeUntilDestroyed()
    ),
    { initialValue: null as PartnerFlatFeeSimulationResponse | null }
  );

  /** Clique num botão de prazo: grava no formulário, que dispara uma nova simulação. */
  selectTerm(value: string): void {
    this.form.controls.termYears.setValue(value);
  }
}

/** Valor do campo como número; vazio, inválido ou negativo conta como zero. */
function toAmount(value: unknown): number {
  const amount = Number(value);
  return Number.isFinite(amount) && amount > 0 ? amount : 0;
}
